import type { SlideRubric } from '@breakoutlearning/firebase-repository/models/SlideRubric'
import type { SlideDeckRole } from '@breakoutlearning/firebase-repository/models/SlideDeckRole'
import type { FirestoreAssignmentRubric } from '@breakoutlearning/firebase-repository/models/AssignmentRubric'
import { observer } from 'mobx-react-lite'
import { BreakoutButton } from 'components/design-system/BreakoutButton'
import { BreakoutInfoTooltip } from 'components/design-system/BreakoutPrefabTooltips'
import { SpinnerCentered } from 'components/Spinner'
import { useBreakoutUser } from 'hooks/profile'
import { useCubitBuilder } from 'hooks/cubits'
import { useLTIMode } from 'hooks/use-lti-mode'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslationTyped } from 'i18n/i18n'
import { Trans } from 'react-i18next'
import { toast } from 'react-hot-toast'
import { ConfigureWeightsDialog } from './ConfigureWeightsDialog'
import { ScoreMappingScales } from './ScoreMappingScales'
import { ScoreMappingDefaultsCubit } from './ScoreMappingDefaultsCubit'
import {
  ALL_ROLES_COLUMN,
  SCALES,
  applicableRoleIdsForRubric,
  columnSum,
  equalSplit,
  isRubricEligibleForCustomGrading,
  isWeightGridValid,
  rubricAppliesToColumn,
  scaleForRubricType,
  type CustomRubricsConfig,
  type RubricConfigPayload,
  type ScaleDef,
  type WeightColumn,
  type WeightGrid,
} from './customRubricsConfig'

type ConfigureCustomRubricsProps = {
  rubrics: SlideRubric[]
  roles: SlideDeckRole[]
  existingConfig?: Map<string, FirestoreAssignmentRubric>
  disabled?: boolean
  configRef: React.MutableRefObject<CustomRubricsConfig>
  onValidityChange?: (isValid: boolean) => void
  /** Fires when the weights/score-mapping have diverged from the initial
   * config — i.e. there are unsaved custom-rubric changes. The weights dialog's
   * own Save only commits to in-memory state, so the parent uses this to warn
   * before the grading dialog is closed. */
  onDirtyChange?: (isDirty: boolean) => void
  /** Controlled open state for the weights dialog (triggered from the grading
   * column's "Customize weights" link). */
  weightsDialogOpen?: boolean
  onWeightsDialogClose?: () => void
  /** Whether the Settings → Instructor Defaults page is reachable from this
   * surface. False in the LTI flow, where that page can't be opened — the
   * "What are defaults?" tooltip then omits the reference to it. */
  defaultsManageableInSettings?: boolean
  /** Seed each scale that has no per-assignment override from the instructor's
   * saved score-mapping defaults. True for assignments that have never been
   * scored — new assignments and unpublished drafts; false for a published
   * assignment, which must seed from its own override, else the global default
   * (what scoring actually uses), so that changing the defaults never
   * retroactively re-scores it.
   *
   * This must be passed explicitly: it cannot be inferred from `existingConfig`,
   * which is undefined for any assignment not gated into `usesCustomRubrics`
   * (see InstructorAssignmentCubit.customRubricsById) and so does not distinguish
   * "no override yet" from "seed from defaults". Defaults to false so a caller
   * that forgets simply doesn't seed from defaults, rather than silently
   * re-scoring an assignment. */
  seedFromInstructorDefaults?: boolean
}

/**
 * The per-assignment custom-grading panel: a score-mapping editor (for the
 * scales present in the assignment) plus the weights grid. The score-mapping
 * lifecycle — loading the instructor's saved defaults, seeding, validity,
 * dirtiness, and "Save as Defaults" — lives in a shared ScoreMappingDefaultsCubit
 * so this and the Settings subpage don't reimplement it; the weight grid stays
 * local. While the defaults load the panel reports itself not-yet-valid (keeping
 * the dialog's Save disabled) and shows a spinner.
 */
export const ConfigureCustomRubrics = observer(function ConfigureCustomRubrics({
  rubrics,
  roles,
  existingConfig,
  disabled,
  configRef,
  onValidityChange,
  onDirtyChange,
  weightsDialogOpen = false,
  onWeightsDialogClose,
  defaultsManageableInSettings = true,
  seedFromInstructorDefaults = false,
}: ConfigureCustomRubricsProps) {
  const { tt } = useTranslationTyped()
  const user = useBreakoutUser()
  const { ltiModeOn } = useLTIMode()
  // Settings → Instructor Defaults is unreachable from the LTI-embedded flow, so
  // the tooltip omits the reference there — whether this panel is hosted by an
  // LTI-specific dialog (via the prop) or the shared edit dialog running inside
  // the LTI iframe (detected here).
  const showSettingsHint = defaultsManageableInSettings && !ltiModeOn

  // Defaults are read from the section instructor's profile, so saving them
  // only makes sense for instructors. Any other role (TA, admin, corre) would
  // write to a profile nothing reads; the endpoint denies them.
  const canSaveDefaults = user.isInstructor

  const eligibleRubrics = useMemo(
    () => rubrics.filter(isRubricEligibleForCustomGrading),
    [rubrics]
  )

  const scalesPresent = useMemo(
    () =>
      SCALES.filter((scale) =>
        eligibleRubrics.some((r) =>
          scale.rubricTypes.includes(r.data.rubricType)
        )
      ),
    [eligibleRubrics]
  )

  // The assignment's existing mapping for a scale, used to seed the editor
  // ahead of the saved default / global default.
  const assignmentMappingForScale = useCallback(
    (scale: ScaleDef): Record<string, number> | undefined => {
      if (!existingConfig) return undefined
      const rubricOfScale = eligibleRubrics.find((r) =>
        scale.rubricTypes.includes(r.data.rubricType)
      )
      if (!rubricOfScale) return undefined
      return existingConfig.get(rubricOfScale.id)?.scoreMapping
    },
    [existingConfig, eligibleRubrics]
  )

  // Rebuild (and refetch) only when the *set* of present scales changes, not on
  // every render — `scalesPresent`'s array identity can churn if the parent
  // passes a fresh `rubrics` array. `assignmentMappingForScale` is only read
  // while seeding at construction, so capturing the current one is fine.
  const scalesKey = scalesPresent.map((scale) => scale.id).join('|')
  const cubit = useCubitBuilder(
    () =>
      new ScoreMappingDefaultsCubit(user, {
        scales: scalesPresent,
        seedOverride: assignmentMappingForScale,
        seedFromSavedDefaults: seedFromInstructorDefaults,
      }),
    // seedFromInstructorDefaults is a boolean (value-stable), so this doesn't
    // reintroduce the identity churn that `scalesKey` exists to avoid.
    [user, scalesKey, seedFromInstructorDefaults]
  )

  const columns: WeightColumn[] = useMemo(() => {
    if (roles.length === 0) {
      return [
        {
          key: ALL_ROLES_COLUMN,
          label: tt.instructor_library.custom_rubrics_all_roles(),
        },
      ]
    }
    return roles.map((role) => ({ key: role.id, label: role.data.roleName }))
  }, [roles, tt])

  // Seed the weight grid per rubric from existing config, or an equal split of
  // 100% across all rubrics, per column.
  const defaultGrid: WeightGrid = useMemo(() => {
    const grid: WeightGrid = {}
    for (const rubric of eligibleRubrics) grid[rubric.id] = {}
    // A config that matches NONE of the current rubrics is orphaned (e.g. a
    // deck swap replaced every rubric id while usesCustomRubrics stayed on).
    // Seeding from it would zero every rubric and block saving, so treat it as
    // no config and fall through to the equal-split default.
    //
    // A PARTIAL match intentionally still seeds from the config: the saved
    // weights are real instructor data, and we cannot redistribute them
    // automatically without guessing. A rubric added to the deck seeds at 0 and
    // columns still total 100 (saveable); a removed rubric leaves a column
    // under 100 and the existing sum error asks the instructor to fix it.
    const configMatchesCurrentRubrics =
      existingConfig !== undefined &&
      eligibleRubrics.some((rubric) => existingConfig.has(rubric.id))
    if (existingConfig && configMatchesCurrentRubrics) {
      for (const rubric of eligibleRubrics) {
        const weights = existingConfig.get(rubric.id)?.weights
        for (const col of columns) {
          if (!weights) grid[rubric.id][col.key] = 0
          else if ('allRoles' in weights)
            grid[rubric.id][col.key] = Math.round(weights.allRoles)
          else
            grid[rubric.id][col.key] = Math.round(weights.byRole[col.key] ?? 0)
        }
      }
      return grid
    }
    // No existing config: per column, split 100% across the rubrics applicable
    // to that column (role); non-applicable rubrics stay at 0.
    for (const col of columns) {
      const applicable = eligibleRubrics.filter((r) =>
        rubricAppliesToColumn(r, col.key, roles)
      )
      const split = equalSplit(100, applicable.length)
      let i = 0
      for (const rubric of eligibleRubrics) {
        grid[rubric.id][col.key] = rubricAppliesToColumn(rubric, col.key, roles)
          ? split[i++]
          : 0
      }
    }
    return grid
  }, [existingConfig, eligibleRubrics, columns, roles])

  const [grid, setGrid] = useState<WeightGrid>(defaultGrid)

  // Build the payload + validity and publish to the shared ref + parent. Score
  // mappings/validity/dirtiness come from the cubit; the weight grid is local.
  useEffect(() => {
    if (cubit.status === 'loading') {
      onValidityChange?.(false)
      onDirtyChange?.(false)
      return
    }

    const gridValid = isWeightGridValid(grid, columns, eligibleRubrics, roles)
    const isValid = cubit.valid && gridValid

    const rubricConfigs: RubricConfigPayload[] = eligibleRubrics.map(
      (rubric) => {
        const scale = scaleForRubricType(rubric.data.rubricType)
        const rubricGrid = grid[rubric.id] ?? {}

        const weights =
          roles.length === 0
            ? { allRoles: rubricGrid[ALL_ROLES_COLUMN] ?? 0 }
            : {
                // Only the roles this rubric applies to become byRole keys —
                // key presence is role applicability downstream (TS scoring and
                // Go evaluation), so a role-specific rubric must not list roles
                // it doesn't apply to.
                byRole: Object.fromEntries(
                  applicableRoleIdsForRubric(rubric, roles).map((id) => [
                    id,
                    rubricGrid[id] ?? 0,
                  ])
                ),
              }

        return {
          rubricId: rubric.id,
          weights,
          scoreMapping: scale ? cubit.mappings[scale.id] : {},
        }
      }
    )

    // The panel is always shown when rendered, so custom grading is always on.
    configRef.current = { enabled: true, isValid, rubricConfigs }
    onValidityChange?.(isValid)

    // Dirty = weights or score mappings differ from the initial (saved) config.
    const dirty =
      JSON.stringify(grid) !== JSON.stringify(defaultGrid) || cubit.dirty
    onDirtyChange?.(dirty)
  }, [
    cubit.status,
    cubit.mappings,
    cubit.valid,
    cubit.dirty,
    grid,
    columns,
    eligibleRubrics,
    roles,
    configRef,
    onValidityChange,
    onDirtyChange,
    defaultGrid,
  ])

  // Persist the current per-scale score mappings as the user's defaults.
  const handleSaveDefaults = useCallback(async () => {
    const saved = await cubit.save()
    if (saved) {
      toast.success(tt.instructor_library.custom_rubrics_defaults_saved())
    } else {
      toast.error(tt.instructor_library.custom_rubrics_defaults_save_failed())
    }
  }, [cubit, tt])

  if (cubit.status === 'loading') return <SpinnerCentered />
  if (scalesPresent.length === 0) return null

  const weightsInvalid = !isWeightGridValid(
    grid,
    columns,
    eligibleRubrics,
    roles
  )
  const weightsSums = columns.map((col) =>
    columnSum(grid, col.key, eligibleRubrics, roles)
  )

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex flex-row items-center gap-1">
        <h3 className="text-title-medium">
          {tt.instructor_library.custom_rubrics_score_mapping_title()}
        </h3>
        <BreakoutInfoTooltip
          content={tt.instructor_library.custom_rubrics_score_mapping_tooltip()}
        />
      </div>

      <ScoreMappingScales
        scales={scalesPresent}
        values={cubit.mappings}
        disabled={disabled}
        onChange={(scaleId, next) => cubit.changeMapping(scaleId, next)}
      />

      <div className="flex flex-row items-center justify-between gap-3">
        <BreakoutInfoTooltip
          treatReactElementAsString
          label={
            <div className="text-label-medium underline">
              {tt.instructor_library.custom_rubrics_defaults_what_are()}
            </div>
          }
          content={
            showSettingsHint ? (
              <Trans
                i18nKey="instructor_library.custom_rubrics_defaults_tooltip"
                components={{ b: <strong /> }}
              />
            ) : (
              tt.instructor_library.custom_rubrics_defaults_tooltip_lti()
            )
          }
        />
        <BreakoutButton
          kind="secondary"
          size="small"
          disabled={disabled || !cubit.canSave || !canSaveDefaults}
          onClick={handleSaveDefaults}
          data-testid="score-mapping-save-defaults"
        >
          {tt.instructor_library.custom_rubrics_defaults_save()}
        </BreakoutButton>
      </div>

      {/* Defaults belong to the instructor; the endpoint rejects saves from
          other roles (BL-4757), so explain the disabled button instead of
          letting the request 403. */}
      {!canSaveDefaults && (
        <div
          className="text-label-medium text-on-surface-var"
          data-testid="score-mapping-defaults-role-message"
        >
          {tt.instructor_library.custom_rubrics_defaults_role_message()}
        </div>
      )}

      {weightsInvalid && (
        <div className="text-label-medium text-core-error">
          {tt.instructor_library.custom_rubrics_weights_sum_error()} (
          {weightsSums.map((s) => `${s}%`).join(', ')})
        </div>
      )}

      {weightsDialogOpen && (
        <ConfigureWeightsDialog
          rubrics={eligibleRubrics}
          roles={roles}
          initialGrid={grid}
          disabled={disabled}
          onSave={(nextGrid) => {
            setGrid(nextGrid)
            onWeightsDialogClose?.()
          }}
          onClose={() => onWeightsDialogClose?.()}
        />
      )}
    </div>
  )
})
